import React, { useState, useEffect } from "react";
import ForgotPasswordView from "./view";
// @ts-ignore
import { useInput } from "react-hookedup";
import { Navigate, useSearchParams } from "react-router-dom";
import { useForgotPassword, useResetPasswordFromGuid } from "../../mutationsQueries/user";
import { LOGIN_PAGE } from "../../util/constants";
import { UseMutationResult } from "react-query";

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [redirectToLogin, setRedirectToLogin] = useState(false);

  const email = useInput("");
  const password = useInput("");
  const confirmPassword = useInput("");

  // Get query params to determine mode
  const guid = searchParams.get("guid");
  const userId = searchParams.get("user_id");
  const isResetMode = !!(guid && userId);

  // Request reset mutation
  const {
    isSuccess: isRequestSuccess,
    isError: isRequestError,
    mutate: requestReset,
    error: requestError,
    isLoading: isRequestLoading,
  }: UseMutationResult<any, any, { email: string }, unknown> = useForgotPassword();

  // Reset password mutation
  const {
    isSuccess: isResetSuccess,
    isError: isResetError,
    mutate: resetPassword,
    error: resetError,
    isLoading: isResetLoading,
  }: UseMutationResult<any, any, { userId: number; password: string; guid: string }, unknown> =
    useResetPasswordFromGuid();

  // Handle request reset success
  useEffect(() => {
    if (isRequestSuccess) {
      setSuccessMessage("Check your email for password reset instructions.");
      setErrorMessage("");
    }
  }, [isRequestSuccess]);

  // Handle request reset error
  useEffect(() => {
    if (isRequestError) {
      const error = requestError as any;
      if (error?.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage("Failed to send reset email. Please try again.");
      }
      setSuccessMessage("");
    }
  }, [isRequestError, requestError]);

  // Handle password reset success
  useEffect(() => {
    if (isResetSuccess) {
      setSuccessMessage("Password reset successful! Redirecting to login...");
      setErrorMessage("");
      setTimeout(() => {
        setRedirectToLogin(true);
      }, 2000);
    }
  }, [isResetSuccess]);

  // Handle password reset error
  useEffect(() => {
    if (isResetError) {
      const error = resetError as any;
      if (error?.response?.data?.message) {
        const message = error.response.data.message;
        if (message.includes("not found") || message.includes("already used")) {
          setErrorMessage("This reset link is invalid or has already been used. Please request a new password reset.");
        } else {
          setErrorMessage(message);
        }
      } else {
        setErrorMessage("Failed to reset password. Please try again.");
      }
      setSuccessMessage("");
    }
  }, [isResetError, resetError]);

  const onSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    requestReset({ email: email.value });
  };

  const onSubmitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (password.value !== confirmPassword.value) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    resetPassword({
      userId: parseInt(userId as string, 10),
      password: password.value,
      guid: guid as string,
    });
  };

  if (redirectToLogin) return <Navigate to={LOGIN_PAGE} />;

  if (isResetMode) {
    return (
      <ForgotPasswordView
        mode="reset"
        password={password}
        confirmPassword={confirmPassword}
        onSubmit={onSubmitReset}
        errorMessage={errorMessage}
        successMessage={successMessage}
        isLoading={isResetLoading}
      />
    );
  }

  return (
    <ForgotPasswordView
      mode="request"
      email={email}
      onSubmit={onSubmitRequest}
      errorMessage={errorMessage}
      successMessage={successMessage}
      isLoading={isRequestLoading}
    />
  );
};

export default ForgotPasswordPage;
